import type { JSX, PropsWithChildren } from 'react';
import { useCallback } from 'react';
import { styled } from 'styled-components';
import { Root as ToggleGroupRoot } from '@radix-ui/react-toggle-group';
import { useColorPickerState } from './ColorPickerContext';
import { convertToHsv } from './color-conversion-utils';

const Container = styled(ToggleGroupRoot)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
`;

export type ColorListProps = PropsWithChildren;

/**
 * Displays color options as a vertical list of labeled rows. Use
 * `ColorListGroup` to organize options into labeled sections, and
 * `ColorListOption` for individual items.
 */
export const ColorList = ({ children }: ColorListProps): JSX.Element => {
  const { valueAsHex, onChangeNonDerivedValueAsHsv } = useColorPickerState();

  const onValueChange = useCallback(
    (value: string) => {
      if (value === '') {
        // Bail out. This happens because we're using Radix ToggleGroup and
        // clicking the active item sets value to "", but we don't want to unset
        // the selected color.
        return;
      }

      const hsv = convertToHsv(value);
      onChangeNonDerivedValueAsHsv(hsv);
    },
    [onChangeNonDerivedValueAsHsv],
  );

  return (
    <Container
      onValueChange={onValueChange}
      type="single"
      value={valueAsHex}
    >
      {children}
    </Container>
  );
};

ColorList.displayName = 'ColorList';
